import React, { useState, useEffect } from "react";
import SVG from "src/utils/SVG";
import styled from "styled-components";
import IconButton from "./IconButton";
import imageCompression from "browser-image-compression";
import type { ImageData } from "src/vo/userVo";

const imageServerIp = process.env.REACT_APP_IMAGE_SERVER_IP;

// 상수 정의
const MAX_FILE_SIZE_KB = 10000; // 10MB
const MAX_TOTAL_SIZE_KB = 150000; // 150MB

interface IStoreImageUploadProps {
  className?: string;
  imageList: (File | ImageData)[];
  setImageList: (files: (File | ImageData)[]) => void;
  maxImages?: number;
  onDeleteImage?: (imageId: number) => void;
}

const compressionOption = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  initialQuality: 0.7,
};

const StoreImageUpload = (props: IStoreImageUploadProps) => {
  const { imageList, setImageList, maxImages = 3, onDeleteImage } = props;
  const [localImageList, setLocalImageList] = useState<(File | ImageData)[]>(imageList);

  useEffect(() => {
    setLocalImageList(imageList);
  }, [imageList]);

  useEffect(() => {
    setImageList(localImageList);
  }, [localImageList, setImageList]);

  const saveFileImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectFiles = e.target.files;
    if (selectFiles !== null) {
      try {
        if (selectFiles.length > 0) {
          // 최대 이미지 수 체크
          if (selectFiles.length + localImageList.length > maxImages) {
            alert(`최대 ${maxImages}개의 이미지만 업로드할 수 있습니다.`);
            return;
          }

          let totalFileSize = 0;
          for (let i = 0; i < selectFiles.length; i++) {
            totalFileSize += Number(selectFiles[i].size);
            const fileType = selectFiles[i].type;
            const oneFileSize = Number(selectFiles[i].size / 1024);
            
            // 이미지 파일만 허용
            if (!String(fileType).includes("image")) {
              alert("이미지 파일만 업로드할 수 있습니다.");
              return;
            }
            
            if (oneFileSize > MAX_FILE_SIZE_KB) {
              alert("하나의 이미지 파일이 10MB보다 클 수 없습니다.");
              return;
            }
            
            if (totalFileSize / 1024 > MAX_TOTAL_SIZE_KB) {
              alert("전체 파일 사이즈가 150MB를 넘길 수 없습니다.");
              return;
            }
          }

          const newFiles: File[] = [];
          for (let i = 0; i < selectFiles.length; i++) {
            const file = selectFiles[i];
            const compressedFile = await imageCompression(file, compressionOption);
            const newFile = new File([compressedFile], compressedFile.name, { type: file.type });
            newFiles.push(newFile);
          }
          
          setLocalImageList((images) => [...images, ...newFiles]);
        }
      } catch (error) {
        alert("이미지 업로드 실패 다시 시도해 주세요");
        console.error("imageUpload Error", error);
      }
    }
    
    // input 값 초기화
    e.target.value = "";
  };

  const deleteFileImage = (selectedImage: File | ImageData) => {
    // 기존 이미지(ImageData)를 삭제하는 경우 ID를 콜백으로 전달
    if (!(selectedImage instanceof File) && onDeleteImage && selectedImage.img_idx) {
      onDeleteImage(selectedImage.img_idx);
    }
    
    const removedList = localImageList.filter((image) => image !== selectedImage);
    setLocalImageList(removedList);
  };

  const canAddMore = localImageList.length < maxImages;

  return (
    <StoreImageUploadContainer>
      {canAddMore && (
        <UploadButton htmlFor="store-image-file">
          <span>이미지 추가</span>
          <input
            id="store-image-file"
            type="file"
            accept="image/*"
            name="store_images"
            onChange={saveFileImage}
            multiple
          />
        </UploadButton>
      )}
      
      {localImageList.length > 0 && (
        <PreviewGrid>
          {localImageList.map((image: File | ImageData, index: number) => {
            const isFile = image instanceof File;
            const imageUrl = isFile 
              ? URL.createObjectURL(image as File)
              : imageServerIp + (image as ImageData).img_url;
              
            return (
              <PreviewItem key={index}>
                <ImageWrapper>
                  <img alt="매장 이미지" src={imageUrl} />
                </ImageWrapper>
                <DeleteButton
                  btnName="이미지 삭제"
                  onClick={() => deleteFileImage(image)}
                >
                  <SVG name="thumbDel" width={20} height={20} viewBox="0 0 24 24" />
                </DeleteButton>
              </PreviewItem>
            );
          })}
        </PreviewGrid>
      )}
      
      <ImageCounter>
        {localImageList.length} / {maxImages}
      </ImageCounter>
    </StoreImageUploadContainer>
  );
};

const StoreImageUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 16px;
  border: 1px dashed ${({ theme }) => theme.color.lineSecondary};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.color.bgPrimary};
  color: ${({ theme }) => theme.color.secondary};
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:hover {
    border-color: ${({ theme }) => theme.color.primary};
  }
  
  > input[type="file"] {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
  max-width: 100%;
`;

const PreviewItem = styled.div`
  position: relative;
  aspect-ratio: 1;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border: 1px solid ${({ theme }) => theme.color.lineSecondary};
  border-radius: 4px;
  overflow: hidden;
  
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DeleteButton = styled(IconButton)`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background-color: ${({ theme }) => theme.color.warning};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    color: white;
  }
`;

const ImageCounter = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.tertiary};
  text-align: right;
`;

export default StoreImageUpload;